import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api.ts'
import { money } from '../format.ts'

interface OrderRow {
  id: string
  number: string
  customerName: string
  salesCode: string | null
  createdAt: string
  status: string
  itemsTotal: number
  itemsCancelled: number
  itemsOrdered: number
  deliveryAtRisk: boolean
}
const STATUS_LABEL: Record<string, string> = {
  nowe: 'nowe',
  w_realizacji: 'w realizacji',
  anulowane: 'anulowane',
  zrealizowane: 'zrealizowane',
}

const STATUS_STYLE: Record<string, string> = {
  nowe: 'bg-slate-100 text-slate-600',
  w_realizacji: 'bg-blue-100 text-blue-700',
  anulowane: 'bg-slate-100 text-slate-400',
  zrealizowane: 'bg-green-100 text-green-700',
}

/** Customer orders (ZK) — what clients bought, and how far along each one is. */
export function OrdersPage() {
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get<{ orders: OrderRow[] }>(`/api/customer-orders${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`)
        .then((r) => setOrders(r.orders))
        .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
    }, 120)
    return () => clearTimeout(t)
  }, [search])

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Zamówienia klientów</h1>
      <p className="text-sm text-slate-500 mb-4">
        Powstają z zaakceptowanych ofert. Z zamówienia klienta zamawiasz maszyny u producentów.
      </p>

      <input
        type="search"
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Szukaj: numer zamówienia, klient…"
        className="w-full max-w-md px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600 mb-3"
      />

      {error && <div className="text-sm text-red-700 mb-3">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400">
            <tr>
              <th className="text-left font-medium px-4 py-2">Numer</th>
              <th className="text-left font-medium px-4 py-2">Klient</th>
              <th className="text-left font-medium px-4 py-2">Maszyny</th>
              <th className="text-left font-medium px-4 py-2">Handlowiec</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2">
                  <Link to={`/zamowienia/${o.id}`} className="text-brand-700 font-medium hover:underline">
                    {o.number}
                  </Link>
                </td>
                <td className="px-4 py-2 text-slate-700">{o.customerName}</td>
                <td className="px-4 py-2 text-slate-500">
                  {/* Cancelled machines are not waiting to be ordered — they leave the denominator. */}
                  {o.itemsOrdered}/{o.itemsTotal - o.itemsCancelled} zamówione
                  {o.itemsCancelled > 0 && <span className="text-slate-400"> · {o.itemsCancelled} anul.</span>}
                </td>
                <td className="px-4 py-2 text-slate-400">{o.salesCode}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLE[o.status] ?? ''}`}>
                    {STATUS_LABEL[o.status] ?? o.status}
                  </span>
                  {o.deliveryAtRisk && (
                    <span className="ml-2 text-xs text-red-700" title="Producent potwierdził termin późniejszy niż obiecany klientowi">
                      ⚠ termin zagrożony
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Brak zamówień. Powstają z zaakceptowanych ofert.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
